import { AbstractEntityService } from "./AbstractEntityService";
import { restService } from "./RestService";

export class PropertyService extends AbstractEntityService {
    constructor(baseUrl) {
        super();
        this.baseUrl = baseUrl;
    }

    // GET /api/properties
    async findAll() {
        const url = `${this.baseUrl}/properties`;
        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, "Unable to load Properties - please see server logs for more details");
        }
    }

    // GET /api/properties/{id}
    async findById(id) {
        const url = `${this.baseUrl}/properties/${id}`;
        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, "Unable to load Property - please see server logs for more details");
        }
    }

    // POST /api/properties (JSON or multipart when logoFile provided)
    async create(property, logoFile) {
        const url = `${this.baseUrl}/properties`;
        if (logoFile) {
            const formData = new FormData();
            formData.append('property', new Blob([JSON.stringify({ name: property.name, description: property.description })], { type: 'application/json' }));
            formData.append('logo', logoFile);
            try {
                return await restService.post(url, formData, { 'Content-Type': 'multipart/form-data' });
            } catch (error) {
                this.handleError(error, "Unable to create Property with logo - please see server logs for more details");
            }
        } else {
            try {
                return await super.create(url, property);
            } catch (error) {
                this.handleError(error, "Unable to create Property - please see server logs for more details");
            }
        }
    }

    // PUT /api/properties/{id}
    async update(id, property) {
        const url = `${this.baseUrl}/properties/${id}`;
        try {
            return await super.update(url, property);
        } catch (error) {
            this.handleError(error, "Unable to update Property - please see server logs for more details");
        }
    }

    // DELETE /api/properties/{id}
    async delete(id) {
        const url = `${this.baseUrl}/properties/${id}`;
        try {
            return await super.delete(url);
        } catch (error) {
            this.handleError(error, "Unable to delete Property - please see server logs for more details");
        }
    }

    // PUT /api/properties/{id}/logo
    async uploadLogo(propertyId, file) {
        const url = `${this.baseUrl}/properties/${propertyId}/logo`;
        const formData = new FormData();
        formData.append('file', file);
        try {
            return await restService.put(url, formData, { 'Content-Type': 'multipart/form-data' });
        } catch (error) {
            this.handleError(error, "Unable to upload logo - please see server logs for more details");
        }
    }

    // GET /api/properties/{id}/pictures — returns List<PicturesAlbum>
    async getPictures(propertyId) {
        const url = `${this.baseUrl}/properties/${propertyId}/pictures`;
        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, "Unable to load property pictures - please see server logs for more details");
        }
    }

    // GET /api/properties/{id}/pictures/{albumId} — returns PicturesAlbum
    async getPicturesAlbum(propertyId, albumId) {
        const url = `${this.baseUrl}/properties/${propertyId}/pictures/${albumId}`;
        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, "Unable to load pictures album - please see server logs for more details");
        }
    }

    // POST /api/properties/{id}/pictures/{albumId} (JSON) — creates album, returns PicturesAlbum
    async createPicturesAlbum(propertyId, albumId, metadata) {
        const url = `${this.baseUrl}/properties/${propertyId}/pictures/${albumId}`;
        try {
            return await restService.post(url, metadata);
        } catch (error) {
            this.handleError(error, "Unable to create pictures album - please see server logs for more details");
        }
    }

    // POST /api/properties/{id}/pictures/{albumId} (multipart) — uploads pictures, returns PicturesAlbum
    async uploadPictures(propertyId, albumId, files) {
        const url = `${this.baseUrl}/properties/${propertyId}/pictures/${albumId}`;
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        try {
            return await restService.post(url, formData, { 'Content-Type': 'multipart/form-data' });
        } catch (error) {
            this.handleError(error, "Unable to upload pictures - please see server logs for more details");
        }
    }

    // PUT /api/properties/{id}/pictures/{albumId} — updates album metadata, returns PicturesAlbum
    async updatePicturesAlbum(propertyId, albumId, metadata) {
        const url = `${this.baseUrl}/properties/${propertyId}/pictures/${albumId}`;
        try {
            return await restService.put(url, metadata);
        } catch (error) {
            this.handleError(error, "Unable to update pictures album - please see server logs for more details");
        }
    }

    // DELETE /api/properties/{id}/pictures/{albumId} (no body) — deletes entire album, returns 204
    async deletePicturesAlbum(propertyId, albumId) {
        const url = `${this.baseUrl}/properties/${propertyId}/pictures/${albumId}`;
        try {
            return await restService.delete(url);
        } catch (error) {
            this.handleError(error, "Unable to delete pictures album - please see server logs for more details");
        }
    }

    // DELETE /api/properties/{id}/pictures/{albumId} (with body) — deletes listed files, returns PicturesAlbum
    async deletePictures(propertyId, albumId, fileNames) {
        const url = `${this.baseUrl}/properties/${propertyId}/pictures/${albumId}`;
        try {
            return await restService.delete(url, fileNames);
        } catch (error) {
            this.handleError(error, "Unable to delete pictures - please see server logs for more details");
        }
    }

    // GET /api/properties/{id}/config
    async getPropertyConfig(propertyId) {
        const url = `${this.baseUrl}/properties/${propertyId}/config`;
        try {
            return await super.find(url);
        } catch (error) {
            this.handleError(error, "Unable to load property configuration - please see server logs for more details");
        }
    }

    // PUT /api/properties/{id}/config
    async updatePropertyConfig(propertyId, config) {
        const url = `${this.baseUrl}/properties/${propertyId}/config`;
        try {
            return await super.update(url, config);
        } catch (error) {
            this.handleError(error, "Unable to update property configuration - please see server logs for more details");
        }
    }

    // GET /api/properties/{id}/bank-transfer-config — returns BankTransferConfig (USER role)
    async getBankTransferConfig(propertyId) {
        const url = `${this.baseUrl}/properties/${propertyId}/bank-transfer-config`;
        try {
            return await super.find(url);
        } catch (error) {
            return null;
        }
    }
}
